/**
 * MSAL Token Service (Phase 1: SharePoint Permissions)
 *
 * Provides token acquisition utilities that can be called from Redux thunks
 * (outside React component context where hooks can't be used).
 */

import type { PublicClientApplication, AccountInfo } from '@azure/msal-browser';

let msalInstance: PublicClientApplication | null = null;

// Backend API scope from environment - must be provided, no fallback
const BACKEND_API_SCOPE: string = import.meta.env.VITE_BACKEND_API_SCOPE;

if (!BACKEND_API_SCOPE) {
  throw new Error("VITE_BACKEND_API_SCOPE environment variable is required but not set. Check your .env.production file.");
}

/**
 * Initialize the MSAL token service with the application's MSAL instance.
 * This should be called once at app startup.
 */
export function initializeMsalTokenService(instance: PublicClientApplication): void {
  msalInstance = instance;
  console.log("✅ MSAL token service initialized");
}

/**
 * Get the current MSAL account (signed-in user).
 */
export function getCurrentMsalAccount(): AccountInfo | undefined {
  if (!msalInstance) {
    console.error("❌ MSAL token service not initialized");
    return undefined;
  }

  const accounts: AccountInfo[] = msalInstance.getAllAccounts();
  return accounts && accounts.length > 0 ? accounts[0] : undefined;
}

/**
 * Acquire a delegated Microsoft Graph token for the current user.
 * Can be called from Redux thunks (non-React context).
 */
export async function acquireDelegatedGraphToken(): Promise<string> {
  if (!msalInstance) {
    throw new Error("MSAL token service not initialized - call initializeMsalTokenService first");
  }

  const account: AccountInfo | undefined = getCurrentMsalAccount();
  if (!account) {
    throw new Error("No MSAL account available - user must sign in first");
  }

  try {
    // Try silent token acquisition first (from cache or refresh token)
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["https://graph.microsoft.com/.default"],
      account: account
    });

    console.log("✅ Acquired Graph token silently");
    return response.accessToken;
  } catch (silentError) {
    console.warn("⚠️ Silent token acquisition failed, trying interactive:", silentError);

    // If silent fails, try interactive popup
    try {
      const response = await msalInstance.acquireTokenPopup({
        scopes: ["https://graph.microsoft.com/.default"],
        account: account
      });

      console.log("✅ Acquired Graph token via popup");
      return response.accessToken;
    } catch (popupError) {
      console.error("❌ Interactive token acquisition failed:", popupError);
      throw new Error(`Failed to acquire Graph token: ${(popupError as Error).message}`);
    }
  }
}

/**
 * Acquire a token for the backend API (for APIM authentication).
 * Can be called from Redux thunks (non-React context).
 */
export async function acquireBackendApiToken(): Promise<string> {
  if (!msalInstance) {
    throw new Error("MSAL token service not initialized - call initializeMsalTokenService first");
  }

  const account: AccountInfo | undefined = getCurrentMsalAccount();
  if (!account) {
    throw new Error("No MSAL account available - user must sign in first");
  }

  try {
    // Try silent token acquisition first (from cache or refresh token)
    const response = await msalInstance.acquireTokenSilent({
      scopes: [BACKEND_API_SCOPE],
      account: account
    });

    console.log("✅ Acquired backend API token silently");
    return response.accessToken;
  } catch (silentError) {
    console.warn("⚠️ Silent token acquisition failed, trying interactive:", silentError);

    // If silent fails, try interactive popup
    try {
      const response = await msalInstance.acquireTokenPopup({
        scopes: [BACKEND_API_SCOPE],
        account: account
      });

      console.log("✅ Acquired backend API token via popup");
      return response.accessToken;
    } catch (popupError) {
      console.error("❌ Interactive token acquisition failed:", popupError);
      throw new Error(`Failed to acquire backend API token: ${(popupError as Error).message}`);
    }
  }
}

/**
 * Acquire a token for the frontend app (for APIM authentication workaround).
 * This uses the User.Read scope that the user has already consented to during login.
 * TEMPORARY: Until admin consent is granted for the backend API scope.
 */
export async function acquireFrontendAppToken(): Promise<string> {
  if (!msalInstance) {
    throw new Error("MSAL token service not initialized - call initializeMsalTokenService first");
  }

  const account: AccountInfo | undefined = getCurrentMsalAccount();
  if (!account) {
    throw new Error("No MSAL account available - user must sign in first");
  }

  try {
    // Acquire token with User.Read scope - this will have frontend app as audience
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["User.Read"],
      account: account
    });

    console.log("✅ Acquired frontend app token silently (audience: frontend app)");
    return response.accessToken;
  } catch (silentError) {
    console.warn("⚠️ Silent token acquisition failed, trying interactive:", silentError);

    try {
      const response = await msalInstance.acquireTokenPopup({
        scopes: ["User.Read"],
        account: account
      });

      console.log("✅ Acquired frontend app token via popup");
      return response.accessToken;
    } catch (popupError) {
      console.error("❌ Interactive token acquisition failed:", popupError);
      throw new Error(`Failed to acquire frontend app token: ${(popupError as Error).message}`);
    }
  }
}
